import * as React from 'react'
import { styled } from 'next-yak'

import { t } from '@owl-nest/localize'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import * as plume from '@ulule/owl-kit-components/next'

import * as projectModel from '../../../../models/project'

import { Contribution } from './Contribution'

type ContributionsBlockProp = {
  project: projectModel.Project
  isOpen: boolean
}

export function ContributionsBlock({
  project,
  isOpen,
}: ContributionsBlockProp): React.ReactElement<ContributionsBlockProp> {
  const orders = project.user_orders || []
  const auth = useAuth()
  const userId = auth.type === 'loggedin' ? auth.user.id : undefined

  return (
    <ContributionsContainer>
      <InformationContainer>
        <plume.styles.heading.XXS asDesktop="S">
          <plume.illustrations.twoToned.HeartWithConfetti size={60} inverted noBackground />
          {t('Thanks for backing this project!')}
        </plume.styles.heading.XXS>

        <ResponsiveWrapper $isOpen={isOpen}>
          {orders.length > 2 ? (
            <>
              <ShrinkContributions>
                {orders.map((order, i) => (
                  <Contribution key={i} project={project} order={order} />
                ))}
              </ShrinkContributions>

              <ShowContributions>
                {userId && (
                  <plume.Link
                    kind="inverted"
                    underlined
                    href={`/users/${userId}/transactions/?project_id=${project.id}`}
                  >
                    {t('See all my contributions to this project')}
                  </plume.Link>
                )}
              </ShowContributions>
            </>
          ) : (
            <>
              {orders.map((order, i) => (
                <Contribution key={i} project={project} order={order} />
              ))}
            </>
          )}

          <LinkContainer>
            <plume.Link href={`/${project.slug}/news/`} kind={'secondary'}>
              {t('See posts')}
            </plume.Link>

            <plume.Link href={`/discussions/compose/${project.owner.id}?order-id=${orders[0].id}`} kind={'secondary'}>
              {t('Contact creator')}
            </plume.Link>
          </LinkContainer>
        </ResponsiveWrapper>
      </InformationContainer>
    </ContributionsContainer>
  )
}

const ContributionsContainer = styled.div`
  display: block;
  text-align: center;
  padding: 10px 0 0;
  width: 100%;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: flex;
    text-align: left;
    padding: 0px;
  }

  // NOT SCREEN !
  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.illustrations.Illustration} {
      margin-bottom: 5px;
    }
  }
`

const InformationContainer = styled.div`
  padding: 0;
  width: 100%;
  box-sizing: border-box;

  ${plume.styles.heading.XXS} {
    display: block;
    margin: 4px auto 22px;
  }

  // NOT SCREEN !
  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.styles.heading.XXS} {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-bottom: 0px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding: 0 20px;

    ${plume.styles.heading.XXS} {
      margin-bottom: 16px;
      margin: 0 auto 6px;

      ${plume.illustrations.Illustration} {
        margin-right: 8px;
      }
    }
  }
`

const LinkContainer = styled(plume.styles.copy.S.regular)`
  margin-left: 8px;
  margin-top: 10px;
  text-align: center;

  ${plume.Link} + ${plume.Link} {
    margin-left: 20px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    text-align: left;
    margin-left: 0;
  }
`

const ShrinkContributions = styled.div`
  max-height: 180px;
  overflow: hidden;
  position: relative;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 30%;
    z-index: 10;
    background: linear-gradient(
      180deg,
      ${() => plume.utils.colors.hexToRgb(plume.COLORS.PRIMARY_GREY_000, 0.1)} 0%,
      ${() => plume.utils.colors.hexToRgb(plume.COLORS.PRIMARY_GREY_000, 1)} 80%
    );
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-top: 0;
  }
`

const ShowContributions = styled(plume.styles.copy.S.regular)`
  display: block;
  text-align: center;
  position: relative;
  margin-top: -20px;

  ${plume.Link} {
    color: ${plume.COLORS.TEXT_SECONDARY};
  }
`

const ResponsiveWrapper = styled.div<{ $isOpen: boolean }>`
  // NOT SCREEN !
  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-top: ${(props) => (props.$isOpen ? '20px' : '0')};
    display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  }
`
